import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { 
  getTournamentsV2,
  registerForTournament as registerForTournamentV2,
  withdrawFromTournament,
  getTournamentEntrants
} from '../services/tournamentsV2';
import { TournamentV2, TournamentEntrant } from '../types';
import './TournamentRegistration.css';

interface TournamentRegistrationProps {
  clubId: number;
}

const TournamentRegistration: React.FC<TournamentRegistrationProps> = ({ clubId }) => {
  const { t } = useTranslation();
  const { player } = useAuth();
  const [tournaments, setTournaments] = useState<TournamentV2[]>([]);
  const [playerRegistrations, setPlayerRegistrations] = useState<Record<number, TournamentEntrant | null>>({});
  const [entrantCounts, setEntrantCounts] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registering, setRegistering] = useState<Record<number, boolean>>({});

  // Load tournaments and registration status
  const loadData = async () => {
    if (!player) return;

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Loading tournament data (V2)...');
      const tournamentsData = await getTournamentsV2(clubId);
      console.log('📊 Loaded tournament data:', tournamentsData);
      setTournaments(tournamentsData);

      // Check registration status for each tournament
      const registrations: Record<number, TournamentEntrant | null> = {};
      const counts: Record<number, number> = {};
      for (const tournament of tournamentsData) {
        try {
          const entrants = await getTournamentEntrants(tournament.id);
          const playerEntrant = entrants.find(e => e.player_id === player.id);
          registrations[tournament.id] = playerEntrant || null;
          // Count confirmed and registered entrants
          counts[tournament.id] = entrants.filter(e => 
            e.status === 'confirmed' || e.status === 'registered'
          ).length;
        } catch (err) {
          console.error(`Error checking registration for tournament ${tournament.id}:`, err);
          registrations[tournament.id] = null;
          counts[tournament.id] = 0;
        }
      }
      setPlayerRegistrations(registrations);
      setEntrantCounts(counts);
    } catch (err) {
      console.error('Error loading tournament data:', err);
      setError(t('tournaments.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [clubId, player]);

  // Make loadData available globally for debugging
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).refreshTournamentRegistration = loadData;
    }
  }, [loadData]);

  // Register for tournament
  const handleRegister = async (tournamentId: number) => {
    if (!player) return;

    const confirmed = window.confirm(t('tournaments.registration.registrationConfirmMessage'));
    if (!confirmed) return;

    try {
      setRegistering(prev => ({ ...prev, [tournamentId]: true }));
      
      await registerForTournamentV2(tournamentId, player.id);
      await loadData(); // Reload to get updated registration status
      
      // Show success message
      console.log(t('tournaments.registration.registrationSuccess'));
    } catch (err) {
      console.error('Error registering for tournament:', err);
      setError(t('tournaments.registration.registrationError'));
    } finally {
      setRegistering(prev => ({ ...prev, [tournamentId]: false }));
    }
  };

  // Unregister from tournament
  const handleUnregister = async (tournamentId: number) => {
    if (!player) return;

    const confirmed = window.confirm(t('tournaments.registration.unregistrationConfirmMessage'));
    if (!confirmed) return;

    try {
      setRegistering(prev => ({ ...prev, [tournamentId]: true }));
      
      const registration = playerRegistrations[tournamentId];
      if (registration) {
        await withdrawFromTournament(registration.id);
      }
      await loadData(); // Reload to get updated registration status
      
      // Show success message
      console.log(t('tournaments.registration.unregistrationSuccess'));
    } catch (err) {
      console.error('Error unregistering from tournament:', err);
      setError(t('tournaments.registration.unregistrationError'));
    } finally {
      setRegistering(prev => ({ ...prev, [tournamentId]: false }));
    }
  };

  // Check if registration is possible
  const canRegister = (tournament: TournamentV2): { can: boolean; reason?: string } => {
    const now = new Date();
    const tournamentStart = new Date(tournament.start_date);

    // Debug logging
    console.log('Registration check for:', tournament.name);
    console.log('Current time:', now.toISOString());
    console.log('Tournament start:', tournamentStart.toISOString());

    // Check if already registered
    if (playerRegistrations[tournament.id]) {
      console.log('Already registered');
      return { can: false, reason: t('tournaments.registration.alreadyRegistered') };
    }

    // Check if tournament status allows registration
    if (tournament.status !== 'upcoming') {
      console.log('Tournament not in upcoming status');
      return { can: false, reason: t('tournaments.registration.registrationClosed') };
    }

    // Check if tournament has already started
    if (now > tournamentStart) {
      console.log('Registration closed (tournament started)');
      return { can: false, reason: t('tournaments.registration.registrationClosed') };
    }

    console.log('Registration is allowed!');
    return { can: true };
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  // Format datetime for display
  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="tournament-registration">
        <div className="loading">{t('tournaments.loading')}</div>
      </div>
    );
  }

  return (
    <div className="tournament-registration">
      <div className="registration-header">
        <h2>{t('tournaments.registration.title')}</h2>
        <p>Register for tournaments in your club</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {tournaments.length === 0 ? (
        <div className="empty-state">
          <p>No tournaments available for registration.</p>
        </div>
      ) : (
        <div className="tournaments-grid">
          {tournaments.map(tournament => {
            const registration = playerRegistrations[tournament.id];
            const registrationStatus = canRegister(tournament);
            // Get participant count from settings (V2)
            const maxParticipants = tournament.settings?.participant_count || 16;
            const currentParticipants = entrantCounts[tournament.id] || 0;
            const spotsRemaining = maxParticipants - currentParticipants;

            return (
              <div key={tournament.id} className="tournament-card">
                <div className="tournament-card-header">
                  <h3>{tournament.name}</h3>
                  <div className="tournament-status">
                    {registration ? (
                      <span className="badge badge-registered">
                        {t('tournaments.registration.registered')}
                      </span>
                    ) : (
                      (() => {
                        const registrationStatus = canRegister(tournament);
                        if (registrationStatus.can) {
                          return (
                            <span className="badge badge-open">
                              {t('tournaments.registration.registrationOpen')}
                            </span>
                          );
                        } else {
                          // Determine the appropriate badge based on the reason
                          if (registrationStatus.reason === t('tournaments.registration.registrationNotOpen')) {
                            return (
                              <span className="badge badge-closed">
                                {t('tournaments.registration.registrationNotOpen')}
                              </span>
                            );
                          } else if (registrationStatus.reason === t('tournaments.registration.registrationClosed')) {
                            return (
                              <span className="badge badge-closed">
                                {t('tournaments.registration.registrationClosed')}
                              </span>
                            );
                          } else if (registrationStatus.reason === t('tournaments.registration.tournamentFull')) {
                            return (
                              <span className="badge badge-full">
                                {t('tournaments.registration.tournamentFull')}
                              </span>
                            );
                          } else {
                            return (
                              <span className="badge badge-closed">
                                {t('tournaments.registration.registrationClosed')}
                              </span>
                            );
                          }
                        }
                      })()
                    )}
                  </div>
                </div>

                <div className="tournament-card-body">
                  {tournament.description && (
                    <div className="tournament-description">
                      <p>{tournament.description}</p>
                    </div>
                  )}

                  <div className="tournament-info">
                    <div className="info-row">
                      <span className="info-label">{t('tournaments.registration.format')}:</span>
                      <span className="info-value">{t(`tournaments.format.${tournament.format}`)}</span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">{t('tournaments.registration.participants')}:</span>
                      <span className="info-value">
                        {currentParticipants} / {maxParticipants}
                        {spotsRemaining > 0 && (
                          <span className="spots-remaining">
                            ({spotsRemaining} {t('tournaments.registration.spotsRemaining')})
                          </span>
                        )}
                      </span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">{t('tournaments.registration.tournamentDate')}:</span>
                      <span className="info-value">{formatDateTime(tournament.start_date)}</span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">Status:</span>
                      <span className="info-value">{t(`tournaments.status.${tournament.status}`)}</span>
                    </div>
                  </div>
                </div>

                <div className="tournament-card-footer">
                  {registration ? (
                    <button
                      className="btn btn-danger"
                      onClick={() => handleUnregister(tournament.id)}
                      disabled={registering[tournament.id]}
                    >
                      {registering[tournament.id] ? 'Processing...' : t('tournaments.registration.unregister')}
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleRegister(tournament.id)}
                      disabled={!registrationStatus.can || registering[tournament.id]}
                      title={registrationStatus.reason}
                    >
                      {registering[tournament.id] ? 'Processing...' : t('tournaments.registration.register')}
                    </button>
                  )}

                  {!registrationStatus.can && !registration && (
                    <div className="registration-reason">
                      <span className="reason-text">{registrationStatus.reason}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TournamentRegistration;
